import * as XLSX from 'xlsx';
import { v4 as uuid } from 'uuid';

export function replaceSpaces(value) {
  return value?.replaceAll(/ /ig,'_');
}

export function updateKey(key, validate = false) {
  key = key.toLowerCase();
  switch (true) {
    case key.includes('issue title'):
      return 'title';

    case key.includes('latitude'):
      return 'geolocation_latitude';

    case key.includes('longitude'):
      return 'geolocation_longitude';

    case key.includes('description'):
      return 'description';

    case key.includes('recommendation'):
      return 'recommendation';

    case key.includes('material'):
      return 'element_material';

    case key.includes('class'):
      return 'element_class';

    default:
      return validate ? '' : key.replaceAll(/ /ig,'_');
  }
}

export function updateValue(key, value) {
  key = key.toLowerCase();
  switch (true) {
    case key.includes('material'):
    case key.includes('status'):
    case key.includes('class'):
      return value?.toLowerCase();

    default:
      return value || '';
  }
}

export function validateFileType(acceptedFileTypes, fileType) {
  /**
   * 'every' will stop as soon as the condition is 'false',
   * hence the 'false' condition check and the inversion of the result
   */
  return !(acceptedFileTypes.every(type => !fileType.includes(type)));
}

export function bulkImport(file, callback) {
  const reader = new FileReader();
  const bulkImportData = [];

  reader.onload = (e) => {
    /* Parse data */
    const bstr = e.target.result;
    const wb = XLSX.read(bstr, { type: 'binary' });
    /* Get first worksheet */
    const wsname = wb.SheetNames[0];
    const ws = wb.Sheets[wsname];
    /* Convert array of arrays */
    const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

    /**
     * Validate if uploaded document is the bulk upload template
     */
    const validHeaderItems = [];
    data[0].forEach((item) => !!updateKey(item, true) && validHeaderItems.push(!!updateKey(item, true)));
    const isValidDocument = validHeaderItems.length >= 7;

    if (!isValidDocument) return callback({ valid: isValidDocument, data: bulkImportData });

    data.forEach((row, rowIndex) => {
      if (rowIndex === 0) {
        row.unshift('index');
      }
      if (rowIndex > 0 && Object.keys(row).length > 0) {
        row.unshift(rowIndex);
        /**
         * Map through header items to make sure the index values match for all rows,
         * this helps if the document has empty cells which get ignored when we iterate
         */
        const { rowData, detMechRating } = mapTableData(data, row);
        if (rowData) { bulkImportData.push(compileRowData(rowData, detMechRating)); }
      }
    });

    callback({ valid: isValidDocument, data: bulkImportData });
  };

  reader.readAsBinaryString(file);
}

function mapTableData(data, row) {
  let detMechRating = [];
  const rowData = data[0].map((_, columnIndex) => {
    const detMech = updateKey(data[0][columnIndex]).includes('deterioration_mechanism');
    const detMechCon = updateKey(data[0][columnIndex]).includes('deterioration_mechanism_condition');

    prepareDetMechRating(detMechRating, row, columnIndex, [ detMech, detMechCon ]);
    if (!detMech && !detMechCon) {
      return [updateKey(data[0][columnIndex]), updateValue(data[0][columnIndex], row[columnIndex])];
    }
    return [];
  });
  return { rowData, detMechRating };
}

function prepareDetMechRating(detMechRating, row, index, detMechValidation) {
  const detMech = detMechValidation[0];
  const detMechCon = detMechValidation[1];
  if (detMechCon) {
    /** Update the last created detMechRating object with the detMechRating value */
    let lastItem = detMechRating[detMechRating.length - 1];
    lastItem = {...lastItem, value: row[index]};
    detMechRating[detMechRating.length - 1] = lastItem;
    /** If the object neither has a nor nor value we delete so we don't have unnecessary invalid items */
    if (!lastItem.name && !lastItem.value) detMechRating.splice(detMechRating.length - 1, 1);
  } else if (detMech) {
    /** Create the detMechRating object with just the detMechRating description */
    detMechRating.push({id: uuid(),'name': replaceSpaces(row[index])?.toLowerCase()});
  }
}

function prepareNewObject(rowData) {
  /** Prepare the table data with only usable content */
  const newObject = Object.fromEntries(rowData);
  return Object.entries(newObject).reduce((a,[k,v]) => (v === undefined ? a : (a[k]=v, a)), {});
}

function compileRowData(rowData, detMechRating) {
  const newObject = prepareNewObject(rowData);
  const isValidMaterial = validateMaterial(newObject);
  return {
    ...newObject,
    /** If the material is invalid don't bother adding the detMechRatings */
    'deterioration_mechanism_ratings_attributes': isValidMaterial ? detMechRating : [],
    id: uuid(),
    location_description: '',
    requires_task: false,
    tasks_close_issue: false,
    tasks_attributes: [],
    edit: false,
    det_mech_panel: false,
    new_rating: null,
    images: [],
  };
}

function validateMaterial(newObject) {
  const materialList = ['steel', 'concrete', 'fibreglass', 'slope', 'asbestos', 'timber'];
  return materialList.find(material => material === newObject['element_material']?.toLowerCase());
}
